/**
 * Normalised symbol/exchange cache-key helpers (spec: T005)
 *
 * All retained-registry and demand-registry keys are formatted as
 * "SYMBOL:exchange" using these helpers so that lookups are consistent
 * regardless of how callers capitalise or pad the inputs.
 */

/** Produce a canonical cache key for a symbol + exchange pair */
export function makeEntryKey(symbol: string, exchange: string): string {
  return `${normalizeSymbol(symbol)}:${normalizeExchange(exchange)}`
}

/** Normalise a trading symbol to upper-case, trimmed */
export function normalizeSymbol(symbol: string): string {
  return symbol.trim().toUpperCase()
}

/** Normalise an exchange name to lower-case, trimmed */
export function normalizeExchange(exchange: string): string {
  return exchange.trim().toLowerCase()
}

/**
 * Parse an entry key back to its components.
 * Returns null if the key does not match the expected format.
 */
export function parseEntryKey(
  key: string
): { symbol: string; exchange: string } | null {
  const colonIdx = key.indexOf(':')
  if (colonIdx < 1 || colonIdx === key.length - 1) return null
  return {
    symbol: key.slice(0, colonIdx),
    exchange: key.slice(colonIdx + 1)
  }
}

/**
 * Convert a legacy "SYMBOL-exchange" RealTimePriceStore key (e.g. "BTCUSDT-binance")
 * to the canonical colon-separated format used by the retained registry.
 */
export function legacyKeyToEntryKey(legacyKey: string): string | null {
  const dashIdx = legacyKey.indexOf('-')
  if (dashIdx < 1 || dashIdx === legacyKey.length - 1) return null
  const symbol = normalizeSymbol(legacyKey.slice(0, dashIdx))
  const exchange = normalizeExchange(legacyKey.slice(dashIdx + 1))
  return makeEntryKey(symbol, exchange)
}
